import React from 'react';
import { ArrowRight, Play } from 'lucide-react';
import { Button } from './ui/button';
import { heroData } from '../data/mock';

export const HeroSection = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src={heroData.backgroundImage}
          alt="Students studying together at a Canadian college campus"
          loading="eager"
          decoding="async"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/90 to-blue-50/90"></div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-400/10 rounded-full blur-3xl"></div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-block">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                🎓 Trusted by 1000+ Students Across Canada
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
              {heroData.title}
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed">
              {heroData.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                {heroData.ctaText}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Button>

              <Button 
                onClick={() => scrollToSection('programs')}
                size="lg"
                variant="outline"
                className="border-2 border-gray-300 hover:border-blue-600 text-gray-700 hover:text-blue-600 px-8 py-6 text-lg rounded-lg transition-all duration-300"
              >
                {heroData.secondaryCtaText}
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-200">
              <div>
                <p className="text-3xl font-bold text-gray-900">1000+</p>
                <p className="text-sm text-gray-600 mt-1">Students Placed</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">9</p>
                <p className="text-sm text-gray-600 mt-1">Partner Colleges</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">95%</p>
                <p className="text-sm text-gray-600 mt-1">Success Rate</p>
              </div>
            </div>
          </div>

          {/* Right Content - Logo */}
          <div className="relative lg:block hidden">
            <div className="relative rounded-2xl p-12 flex items-center justify-center min-h-[500px]">
              <img 
                src="https://customer-assets.emergentagent.com/job_elevated-apply/artifacts/myyz19wz_transparent%20logo.png"
                alt="Elevated Education logo"
                loading="eager"
                decoding="async"
                className="w-full max-w-md h-auto object-contain"
              />
            </div>

            {/* Decorative Badge */}
            <div className="absolute -top-6 -right-6 bg-white rounded-full p-6 shadow-xl">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">4.9</p>
                <p className="text-xs text-gray-600">Rating</p>
                <div className="flex mt-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xs">★</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
